export type HelpCategoryId = 'guides' | 'governance' | 'reference' | 'legal';

export interface HelpCategoryMeta {
  readonly id: HelpCategoryId;
  readonly label: string;
  readonly description: string;
}

export interface HelpArticle {
  readonly slug: string;
  readonly title: string;
  readonly summary: string;
  readonly category: HelpCategoryId;
  readonly badge?: 'PLACEHOLDER' | 'Coming Soon' | undefined;
  readonly readTimeMinutes: number;
  readonly markdown: string;
}

export const HELP_CATEGORIES: readonly HelpCategoryMeta[] = [
  {
    id: 'guides',
    label: 'Guides & Operations',
    description: 'Day-to-day setup for organisations, workspaces, product links, and recalls.',
  },
  {
    id: 'governance',
    label: 'Governance & Insights',
    description: 'Team roles, tamper-evident audit trails, and privacy-safe scan analytics.',
  },
  {
    id: 'reference',
    label: 'Reference & API',
    description: 'Plain-English GS1 glossary and Partner API integration reference.',
  },
  {
    id: 'legal',
    label: 'Legal & Privacy',
    description: 'Privacy Notice, Terms of Service, and GDPR Data Processing Summary.',
  },
] as const;

export function getHelpCategoryMeta(categoryId: HelpCategoryId): HelpCategoryMeta {
  const found = HELP_CATEGORIES.find((cat) => cat.id === categoryId);
  return (
    found ?? {
      id: categoryId,
      label: categoryId,
      description: '',
    }
  );
}

/**
 * Strips Markdown formatting characters to produce plain searchable text or snippets.
 */
export function stripMarkdownFormatting(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/```\w*\n?/g, ' '))
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_~`>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Client-side filter for Help Centre articles by search query (title, summary,
 * category label, and full Markdown text) and optional category tab.
 */
export function filterHelpArticles(
  articles: readonly HelpArticle[],
  rawQuery: string,
  categoryFilter: HelpCategoryId | 'all' = 'all',
): readonly HelpArticle[] {
  const normalizedQuery = rawQuery.trim().toLowerCase();
  const queryTokens =
    normalizedQuery.length > 0 ? normalizedQuery.split(/\s+/).filter((t) => t.length > 0) : [];

  return articles.filter((article) => {
    if (categoryFilter !== 'all' && article.category !== categoryFilter) {
      return false;
    }
    if (queryTokens.length === 0) {
      return true;
    }
    const categoryMeta = getHelpCategoryMeta(article.category);
    const searchableText = [
      article.title,
      article.summary,
      categoryMeta.label,
      article.badge ?? '',
      article.markdown,
      stripMarkdownFormatting(article.markdown),
    ]
      .join(' ')
      .toLowerCase();

    return queryTokens.every((token) => searchableText.includes(token));
  });
}
